import { useApi } from '@/api';
import { Link, TableSkeleton, VaultName } from '@/components';
import { tokensMapping } from '@/utils/tokens';
import {
  Box,
  CryptoIcon,
  CurrencyValue,
  DataTable,
  Flex,
  PercentageValue,
  RelativeDateTime
} from '@blockanalitica/ui';
import { TableColumn } from '@blockanalitica/ui/package/types/src/components/molecules/DataTable';

export default function IlkCdpsTable({ pagination, sorting, search }) {
  const [data, error] = useApi({
    url: `/groups/seal/ilks/LSE-MKR-A/cdps/`,
    queryParams: {
      p: pagination.currentPage,
      p_size: pagination.pageSize,
      order: sorting.currentSorting,
      days_ago: 1,
      search: search,
      all: 1
    }
  });

  if (error) {
    throw error;
  }

  if (data) {
    const collateralSymbol = data.collateral_symbol;
    const collateralAddress = data.collateral_address;
    const columns: TableColumn[] = [
      {
        header: 'CDP',
        getCell: (row) => <VaultName cdp={row.cdp} urn={row.urn} />
      },

      {
        header: (
          <Flex
            display="flex"
            flexDirection="row"
            alignItems="center"
            gap="3xs">
            Debt
            <CryptoIcon
              name={tokensMapping('DAI').symbol}
              address={tokensMapping('DAI').address}
              size="0"
            />
          </Flex>
        ),
        getCell: (row) => <CurrencyValue value={row.debt} />,

        cellAlign: 'flex-end',
        headerAlign: 'flex-end',
        sortingField: 'debt'
      },
      {
        header: `Change (1D)`,
        getCell: (row) => (
          <CurrencyValue
            value={row[`debt_change_1d`]}
            options={{ trend: true, iconSize: '0' }}
          />
        ),
        getCellBottom: (row) => (
          <PercentageValue
            color="muted"
            fontSize="-1"
            value={row[`debt_change_1d`] / row.debt}
            options={{
              nullable: (
                <Box color="muted" fontSize="-1">
                  -
                </Box>
              ),
              iconSize: '-1'
            }}
          />
        ),
        cellAlign: 'flex-end',
        headerAlign: 'flex-end',
        sortingField: `debt_change_1d`
      },
      {
        header: (
          <Flex
            display="flex"
            flexDirection="row"
            alignItems="center"
            gap="3xs">
            Collateral
            <CryptoIcon
              name={collateralSymbol}
              address={collateralAddress}
              size="0"
            />
          </Flex>
        ),
        getCell: (row) => <CurrencyValue value={row.ink} />,
        getCellBottom: (row) => (
          <CurrencyValue
            fontSize="-1"
            value={row[`ink_change_1d`]}
            options={{
              trend: true,
              nullable: null,
              iconSize: '-1'
            }}
          />
        ),
        cellAlign: 'flex-end',
        headerAlign: 'flex-end',
        sortingField: 'ink'
      },
      {
        header: 'Liquidation Price',
        getCell: (row) => (
          <CurrencyValue
            value={row.liquidation_price}
            options={{
              notation: 'standard',
              currency: 'USD'
            }}
          />
        ),
        cellAlign: 'flex-end',
        headerAlign: 'flex-end',
        sortingField: 'liquidation_price'
      },
      {
        header: 'CR',
        getCell: (row) => (
          <PercentageValue value={row.collateralization_ratio} />
        ),
        cellAlign: 'flex-end',
        headerAlign: 'flex-end',
        sortingField: 'collateralization_ratio'
      },
      {
        header: 'Time',
        getCell: (row) => (
          <Box color="muted">
            <RelativeDateTime value={row.datetime} />
          </Box>
        ),
        sortingField: 'datetime',
        cellAlign: 'flex-end',
        headerAlign: 'flex-end'
      }
    ];

    return (
      <DataTable
        rows={data['results']}
        columns={columns}
        getKey={(row) => row.urn}
        rowLinkProps={{
          to: (row) => row.urn,
          // @ts-ignore
          linkComponent: Link
        }}
        sorting={sorting}
        pagination={{
          ...pagination,
          totalRecords: data.count
        }}
      />
    );
  }
  return <TableSkeleton />;
}
